import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

class StoreStock extends Component {
  /** @type {AbortController | undefined} */
  #activeFetch;

  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest(`.shopify-section, dialog`);

    /** @param {CustomEvent} event */
    const onVariantUpdated = (event) => {
      const variantId = event.detail?.resource?.id;
      if (!variantId) return;
      if (String(variantId) !== this.dataset.variantId) {
        this.dataset.variantId = String(variantId);
      }
      this.#fetchAndRender();
    };

    closestSection?.addEventListener(ThemeEvents.variantUpdate, onVariantUpdated);
    this.disconnectedCallback = () => {
      closestSection?.removeEventListener(ThemeEvents.variantUpdate, onVariantUpdated);
      if (this.#activeFetch) this.#activeFetch.abort();
    };

    this.#fetchAndRender();
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  #getRows() {
    /** @type {Array<{el: HTMLElement, id: string}>} */
    const rows = [];
    this.querySelectorAll('.store-stock__row[data-location-id]').forEach((el) => {
      const id = el.getAttribute('data-location-id') || '';
      if (id) rows.push({ el: /** @type {HTMLElement} */ (el), id });
    });
    return rows;
  }

  /**
   * @param {HTMLElement} rowEl
   * @param {'in' | 'low' | 'out' | 'loading'} status
   */
  #setRowStatus(rowEl, status) {
    const badge = rowEl.querySelector('.store-stock__badge');
    const text = rowEl.querySelector('.store-stock__status');
    if (!badge || !text) return;
    badge.classList.remove('is-in', 'is-low', 'is-out');
    switch (status) {
      case 'in':
        badge.classList.add('is-in');
        text.textContent = 'In Stock';
        break;
      case 'low':
        badge.classList.add('is-low');
        text.textContent = 'Low Stock';
        break;
      case 'out':
        badge.classList.add('is-out');
        text.textContent = 'Out of Stock';
        break;
      default:
        text.textContent = 'Checking…';
    }
  }

  #fetchAndRender() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;
    const rows = this.#getRows();
    if (!rows.length) return;

    rows.forEach(({ el }) => this.#setRowStatus(el, 'loading'));

    const ac = this.#createAbortController();
    const url = `https://admin.saboskirt.com/migrate/store-stock?id=${encodeURIComponent(variantId)}`;

    fetch(url, { signal: ac.signal })
      .then((r) => r.json())
      .then((data) => {
        if (ac.signal.aborted) return;
        const lowThreshold = Number(this.dataset.lowThreshold || 3);
        const items = data?.data?.Items || [];
        rows.forEach(({ el, id }) => {
          const rec = items.find((d) => String(d?.LocationId) === String(id));
          const available = Number(rec?.Available || 0);
          if (available > lowThreshold) this.#setRowStatus(el, 'in');
          else if (available > 0) this.#setRowStatus(el, 'low');
          else this.#setRowStatus(el, 'out');
        });
      })
      .catch(() => {
        if (ac.signal.aborted) return;
        rows.forEach(({ el }) => this.#setRowStatus(el, 'out'));
      });
  }
}

if (!customElements.get('store-stock')) {
  customElements.define('store-stock', StoreStock);
}
